import {Data, Route, Routes} from '@angular/router';
import {MxRoute, MxRoutes, MxRouteTab} from './router.types';
import {isDefined, isPopulatedArray, isString} from '@aviary/store/common/typing.helpers';
import {omitPropertiesByKeyFrom} from '@aviary/store/transformation.helpers';

type UnifiedMxRoute<RouteNameType extends string> = MxRoute<RouteNameType> & {
  children?: readonly MxRoute<RouteNameType>[];
  tabs?: readonly MxRouteTab<RouteNameType>[];
  tabLabel?: string;
};

export function normalizeToAngularRoutes<RouteNameType extends string = string>(mxRoutes: MxRoutes<RouteNameType>): Routes {
  return mxRoutes.map(mxRoute => normalize<RouteNameType>(mxRoute));
}

function normalize<RouteNameType extends string = string>(mxRoute: MxRoute<RouteNameType>): Route {
  const {name, initialQueryParams, children, tabs, tabLabel, ...otherRouteProperties} = mxRoute as UnifiedMxRoute<RouteNameType>;

  const otherRoutePropertiesWithoutData: Omit<Route, 'data'> = omitPropertiesByKeyFrom(otherRouteProperties, 'data');

  const data: Data = {
    ...otherRouteProperties.data,
    name,
    ...isString(tabLabel) && {tabLabel},
    ...isDefined(initialQueryParams) && {initialQueryParams},
  };

  const normalizedChildren: Routes = normalizeChildren<RouteNameType>(children, tabs);

  return {
    ...otherRoutePropertiesWithoutData,
    data,
    ...isPopulatedArray(normalizedChildren) && {children: normalizedChildren}
  };
}

function normalizeChildren<RouteNameType extends string = string>(
  children: readonly MxRoute<RouteNameType>[] | undefined, tabs: readonly MxRouteTab<RouteNameType>[] | undefined
): Routes {
  return [
    ...(tabs ?? []).map(tab => normalize<RouteNameType>(tab)),
    ...(children ?? []).map(child => normalize<RouteNameType>(child))
  ];
}
